import axios from 'axios';

const API_BASE_URL = import.meta.env.REACT_APP_WORDPRESS_URL;
const TOKEN_KEY = 'wp_jwt_token';
const CONSUMER_KEY = import.meta.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY;
const CONSUMER_SECRET = import.meta.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET;

// Create axios instance with default config
const authAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create WooCommerce API instance for customer updates
const wooAPI = axios.create({
  baseURL: `${API_BASE_URL}/wp-json/wc/v3`,
  auth: {
    username: CONSUMER_KEY,
    password: CONSUMER_SECRET,
  },
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
const addAuthInterceptor = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

addAuthInterceptor(authAPI);
addAuthInterceptor(wooAPI);

// Handle token expiration
authAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.href = '/signin';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async login(credentials) {
    try {
      console.log('Starting login process...');
      const response = await authAPI.post('/wp-json/jwt-auth/v1/token', {
        username: credentials.email,
        password: credentials.password,
      });

      const { token, user_email, user_nicename, user_display_name } = response.data;
      
      localStorage.setItem(TOKEN_KEY, token);
      console.log('Token stored in localStorage');
      
      const user = {
        id: response.data.data?.ID || null,
        email: user_email,
        username: user_nicename,
        displayName: user_display_name,
      };

      console.log('Login completed successfully:', user);
      return { token, user };
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error.response?.data?.message || 'Login failed';
      throw new Error(errorMessage);
    }
  },

  async register(userData) {
    try {
      console.log('Starting registration process...');
      const registerResponse = await authAPI.post('/wp-json/custom/v1/register', {
        username: userData.username,
        email: userData.email,
        password: userData.password,
        first_name: userData.firstName,
        last_name: userData.lastName,
      });

      console.log('Registration successful, now logging in...');
      const loginResponse = await this.login({
        email: userData.email,
        password: userData.password,
      });

      console.log('Registration and login completed');
      return loginResponse;
    } catch (error) {
      console.error('Registration failed:', error);
      let errorMessage = 'Registration failed';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.code === 'existing_user_login') {
        errorMessage = 'Username already exists';
      } else if (error.response?.data?.code === 'existing_user_email') {
        errorMessage = 'Email already registered';
      }
      
      throw new Error(errorMessage);
    }
  },

  async forgotPassword(email) {
    try {
      const response = await authAPI.post('/wp-json/bdpwr/v1/reset-password', {
        email,
      });
      
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Password reset failed';
      throw new Error(errorMessage);
    }
  },

  async validateToken() {
    try {
      console.log('Starting token validation...');
      const token = this.getToken();
      if (!token) {
        console.log('No token found');
        return null;
      }

      console.log('Token found, validating with server...');
      const response = await authAPI.post('/wp-json/jwt-auth/v1/token/validate');
      
      if (response.data.data.status === 200) {
        console.log('Token is valid, fetching user data...');
        const userResponse = await authAPI.get('/wp-json/wp/v2/users/me');
        console.log('User data fetched:', userResponse.data);
        
        // Also get WooCommerce customer data for billing/shipping info
        let customerData = {};
        try {
          console.log('Fetching WooCommerce customer data...');
          const customerResponse = await wooAPI.get(`/customers/${userResponse.data.id}`);
          customerData = customerResponse.data;
          console.log('WooCommerce customer data fetched');
        } catch (error) {
          console.warn('Could not fetch customer data:', error);
        }
        
        const userData = {
          id: userResponse.data.id,
          email: userResponse.data.email,
          username: userResponse.data.username,
          displayName: userResponse.data.name,
          firstName: userResponse.data.first_name || customerData.first_name,
          lastName: userResponse.data.last_name || customerData.last_name,
          phone: customerData.billing?.phone || '',
          company: customerData.billing?.company || '',
          address1: customerData.billing?.address_1 || '',
          address2: customerData.billing?.address_2 || '',
          city: customerData.billing?.city || '',
          state: customerData.billing?.state || '',
          postcode: customerData.billing?.postcode || '',
          country: customerData.billing?.country || '',
          shippingAddress1: customerData.shipping?.address_1 || '',
          shippingAddress2: customerData.shipping?.address_2 || '',
          shippingCity: customerData.shipping?.city || '',
          shippingState: customerData.shipping?.state || '',
          shippingPostcode: customerData.shipping?.postcode || '',
          shippingCountry: customerData.shipping?.country || '',
        };
        
        console.log('Complete user data prepared:', userData);
        return userData;
      }
      
      console.log('Token validation failed');
      return null;
    } catch (error) {
      console.error('Token validation failed with error:', error);
      return null;
    }
  },

  async getUserProfile() {
    try {
      console.log('Fetching user profile...');
      const response = await authAPI.get('/wp-json/wp/v2/users/me');
      
      // Get WooCommerce customer data
      let customerData = {};
      try {
        const customerResponse = await wooAPI.get(`/customers/${response.data.id}`);
        customerData = customerResponse.data;
      } catch (error) {
        console.warn('Could not fetch customer data:', error);
      }
      
      return {
        id: response.data.id,
        email: response.data.email,
        username: response.data.username,
        displayName: response.data.name,
        firstName: response.data.first_name || customerData.first_name,
        lastName: response.data.last_name || customerData.last_name,
        phone: customerData.billing?.phone || '',
        company: customerData.billing?.company || '',
        address1: customerData.billing?.address_1 || '',
        address2: customerData.billing?.address_2 || '',
        city: customerData.billing?.city || '',
        state: customerData.billing?.state || '',
        postcode: customerData.billing?.postcode || '',
        country: customerData.billing?.country || '',
        shippingAddress1: customerData.shipping?.address_1 || '',
        shippingAddress2: customerData.shipping?.address_2 || '',
        shippingCity: customerData.shipping?.city || '',
        shippingState: customerData.shipping?.state || '',
        shippingPostcode: customerData.shipping?.postcode || '',
        shippingCountry: customerData.shipping?.country || '',
      };
    } catch (error) {
      throw new Error('Failed to fetch user profile');
    }
  },

  async updateUserProfile(userData) {
    try {
      console.log('Updating user profile:', userData);
      
      // Update WordPress user data
      const wpResponse = await authAPI.post(`/wp-json/wp/v2/users/me`, {
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
      });

      console.log('WordPress user data updated');

      // Update WooCommerce customer data for billing and shipping
      const customerUpdateData = {
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        billing: {
          first_name: userData.firstName,
          last_name: userData.lastName,
          company: userData.company || '',
          address_1: userData.address1 || '',
          address_2: userData.address2 || '',
          city: userData.city || '',
          state: userData.state || '',
          postcode: userData.postcode || '',
          country: userData.country || '',
          email: userData.email,
          phone: userData.phone || '',
        },
        shipping: {
          first_name: userData.firstName,
          last_name: userData.lastName,
          company: userData.company || '',
          address_1: userData.shippingAddress1 || userData.address1 || '',
          address_2: userData.shippingAddress2 || userData.address2 || '',
          city: userData.shippingCity || userData.city || '',
          state: userData.shippingState || userData.state || '',
          postcode: userData.shippingPostcode || userData.postcode || '',
          country: userData.shippingCountry || userData.country || '',
        },
      };

      try {
        await wooAPI.put(`/customers/${wpResponse.data.id}`, customerUpdateData);
        console.log('WooCommerce customer data updated');
      } catch (wooError) {
        console.warn('Could not update WooCommerce customer data:', wooError);
        // Continue even if WooCommerce update fails
      }

      const updatedUserData = {
        id: wpResponse.data.id,
        email: wpResponse.data.email,
        username: wpResponse.data.username,
        displayName: wpResponse.data.name,
        firstName: wpResponse.data.first_name,
        lastName: wpResponse.data.last_name,
        phone: userData.phone || '',
        company: userData.company || '',
        address1: userData.address1 || '',
        address2: userData.address2 || '',
        city: userData.city || '',
        state: userData.state || '',
        postcode: userData.postcode || '',
        country: userData.country || '',
        shippingAddress1: userData.shippingAddress1 || userData.address1 || '',
        shippingAddress2: userData.shippingAddress2 || userData.address2 || '',
        shippingCity: userData.shippingCity || userData.city || '',
        shippingState: userData.shippingState || userData.state || '',
        shippingPostcode: userData.shippingPostcode || userData.postcode || '',
        shippingCountry: userData.shippingCountry || userData.country || '',
      };

      console.log('Profile update completed:', updatedUserData);
      return updatedUserData;
    } catch (error) {
      console.error('Profile update failed:', error);
      const errorMessage = error.response?.data?.message || 'Profile update failed';
      throw new Error(errorMessage);
    }
  },

  logout() {
    console.log('Logging out and clearing token');
    localStorage.removeItem(TOKEN_KEY);
  },

  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  },

  removeToken() {
    localStorage.removeItem(TOKEN_KEY);
  },

  isAuthenticated() {
    return !!this.getToken();
  },
};